import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
} from 'react';

import type { TAiScreenAction, TAiScreenActionHandler } from 'utils/interfaces/IAiScreenAction';
import type IAiScreenActionsProvider from './interfaces/IAiScreenActionsProvider';

interface IAiScreenActions {
  /** Keeps the handler until the returned function is called. */
  register: (handler: TAiScreenActionHandler) => () => void,
  /**
   * Offers the action to every mounted handler. True when one took it. False when none did: the
   * action is then kept for the next handler to mount, so the caller can navigate to the screen
   * that answers it and let that screen take it on arrival.
   */
  dispatch: (action: TAiScreenAction) => boolean,
}

/**
 * How long an action waits for a screen to mount. A navigation takes well under this; an action
 * left longer has no screen coming — the route was refused, say — and must not fire on a screen
 * the user opens on their own later.
 */
export const PENDING_ACTION_TTL_MS = 30_000;

interface IPendingAction {
  action: TAiScreenAction,
  at: number,
}

const AiScreenActionsContext = createContext<IAiScreenActions>({
  register: () => () => {},
  dispatch: () => false,
});

/**
 * Lets the assistant act on a screen: open a panel, press a button.
 *
 * The counterpart of `AiScreenContextProvider`, in the other direction. Handlers and the pending
 * action are kept in refs — no render depends on them — so a screen that registers is not
 * re-rendered by another screen registering.
 *
 * @see [11.6 Screen actions](../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
function AiScreenActionsProvider({ children }: IAiScreenActionsProvider) {
  const handlers = useRef<Set<TAiScreenActionHandler>>(new Set());
  const pending = useRef<IPendingAction | null>(null);

  // The first handler to take the action ends the offer; the rest are not asked.
  const offer = useCallback((action: TAiScreenAction, candidates: TAiScreenActionHandler[]): boolean => (
    candidates.some((handler) => handler(action))
  ), []);

  const register = useCallback((handler: TAiScreenActionHandler) => {
    handlers.current.add(handler);

    const waiting = pending.current;

    // Dropped once taken, or once too old to trust. A handler that refuses it leaves it for the
    // next: one screen registers several, one per action it answers.
    if (waiting && (Date.now() - waiting.at > PENDING_ACTION_TTL_MS || offer(waiting.action, [handler]))) {
      pending.current = null;
    }

    return () => {
      handlers.current.delete(handler);
    };
  }, [offer]);

  const dispatch = useCallback((action: TAiScreenAction): boolean => {
    if (offer(action, Array.from(handlers.current))) {
      pending.current = null;

      return true;
    }

    pending.current = { action, at: Date.now() };

    return false;
  }, [offer]);

  const value = useMemo<IAiScreenActions>(() => ({ register, dispatch }), [dispatch, register]);

  return (
    <AiScreenActionsContext.Provider value={value}>
      {children}
    </AiScreenActionsContext.Provider>
  );
}

/**
 * Answers the assistant's actions for as long as the caller is mounted.
 *
 * The latest handler is the one called, so it can be written inline and close over the screen's
 * state; the registration itself happens once, on mount, and an action left waiting by the chat
 * is offered to it then.
 */
export function useAiScreenAction(handler: TAiScreenActionHandler): void {
  const { register } = useContext(AiScreenActionsContext);
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => register((action) => latest.current(action)), [register]);
}

/** What the chat dispatches through. */
export function useAiScreenActions(): Pick<IAiScreenActions, 'dispatch'> {
  const { dispatch } = useContext(AiScreenActionsContext);

  return useMemo(() => ({ dispatch }), [dispatch]);
}

export default AiScreenActionsProvider;
